import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';

const AuthContext = createContext({});

// Secure token storage: sessionStorage by default, localStorage only when "remember me" is checked
// PWA standalone mode also uses localStorage to persist across refreshes
const TOKEN_KEY = 'token';
const ADMIN_TOKEN_KEY = 'adminToken';
const REMEMBER_KEY = 'rememberMe';

function isStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

function getStoredToken(key) {
  // Check sessionStorage first (more secure), then localStorage (remember me / PWA)
  return sessionStorage.getItem(key) || localStorage.getItem(key);
}

function storeToken(key, value, remember = false) {
  if (remember || isStandaloneMode()) {
    // PWA standalone: always persist in localStorage to survive refreshes
    localStorage.setItem(key, value);
    localStorage.setItem(REMEMBER_KEY, 'true');
  } else {
    sessionStorage.setItem(key, value);
  }
}

function removeToken(key) {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // PWA fix: migrate token from sessionStorage to localStorage if in standalone mode
    if (isStandaloneMode()) {
      const sessionToken = sessionStorage.getItem(TOKEN_KEY);
      if (sessionToken && !localStorage.getItem(TOKEN_KEY)) {
        localStorage.setItem(TOKEN_KEY, sessionToken);
        localStorage.setItem(REMEMBER_KEY, 'true');
      }
    }

    const savedToken = getStoredToken(TOKEN_KEY);
    const savedAdminToken = getStoredToken(ADMIN_TOKEN_KEY);

    if (savedAdminToken) {
      setAdminToken(savedAdminToken);
    }

    if (savedToken) {
      fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      })
        .then(r => {
          if (r.ok) return r.json();
          if (r.status === 401) {
            // Token is truly invalid — remove it
            removeToken(TOKEN_KEY);
            return null;
          }
          // Network error or server error — keep token, retry later
          throw new Error('temp_error');
        })
        .then(userData => {
          if (userData) {
            setUser(userData);
            setToken(savedToken);
          }
          setLoading(false);
        })
        .catch((err) => {
          if (err.message === 'temp_error') {
            // Temporary error — keep token and try to use it
            setToken(savedToken);
          } else {
            // Network error — still keep token for PWA offline resilience
            setToken(savedToken);
          }
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password, remember = false) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      let data;
      try { data = await res.json(); } catch (_e) { data = {}; }
      if (!res.ok) return { success: false, error: data.detail || 'Credenciais inválidas' };

      // Doctor accounts always persist (need push notifications); patient respects remember checkbox
      const shouldPersist = remember || data.user?.role === 'doctor';
      storeToken(TOKEN_KEY, data.token, shouldPersist);
      setUser(data.user);
      setToken(data.token);
      // TikTok Pixel: identify user
      if (window.ttq && data.user?.email) {
        crypto.subtle.digest('SHA-256', new TextEncoder().encode(data.user.email.toLowerCase().trim()))
          .then(buf => {
            const hash = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
            window.ttq.identify({ email: hash, external_id: hash });
          }).catch(() => {});
      }
      return { success: true, user: data.user };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: 'Erro de conexão. Verifique sua internet.' };
    }
  }, []);

  const adminLogin = useCallback(async (email, password, remember = false) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        let msg = 'Credenciais inválidas';
        try { const data = await res.json(); msg = data.detail || msg; } catch (_e) { /* parse error ok */ }
        return { success: false, error: msg };
      }
      const data = await res.json();
      storeToken(ADMIN_TOKEN_KEY, data.token, remember);
      setAdminToken(data.token);
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Credenciais inválidas ou servidor indisponível' };
    }
  }, []);

  const logout = useCallback(() => {
    if (token) {
      fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    removeToken(TOKEN_KEY);
    localStorage.removeItem(REMEMBER_KEY);
    setUser(null);
    setToken(null);
  }, [token]);

  const adminLogout = useCallback(() => {
    if (adminToken) {
      fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
      }).catch(() => {});
    }
    removeToken(ADMIN_TOKEN_KEY);
    setAdminToken(null);
  }, [adminToken]);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
      }
    } catch (err) { console.error('Refresh user error:', err); }
  }, [token]);

  const setUserAndToken = useCallback((userData, sessionToken) => {
    storeToken(TOKEN_KEY, sessionToken, true); // always persist for Google auth
    setUser(userData);
    setToken(sessionToken);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      adminToken,
      loading,
      isAdmin: !!adminToken,
      isDoctor: user?.role === 'doctor',
      login,
      adminLogin,
      logout,
      adminLogout,
      refreshUser,
      setUserAndToken
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
